import { FC, FormEventHandler, useEffect, useState } from "react";
import styles from "./add-contract-aircraft.module.scss";
import { Modal } from "../../../../../ui/modal/Modal";
import { Button } from "../../../../../ui/button/Button";
import { z } from "zod";
import { Snackbar } from "../../../../../ui/snackbar/Snackbar";
import { useAddContractAircraftMutation } from "../contractAircraftApi";
import { useGetAllTransportQuery } from "../../../../../api/transportApi";
import { HHMM2min } from "../../../../../helpers/time";
import Checkbox from "../../../../../ui/Checkbox/Checkbox";
import { ITransport } from "../../../../../interfaces/ITransport";

type Props = {
  contract_id: number;
  close: () => void;
};

type State = {
  errors?: {
    transport_id?: string[];
    count?: string[];
    departmental?: string[];
    limit_time?: string[];
    cost?: string[];
  };
  message?: string | null;
};

const FormSchema = z.object({
  transport_id: z.coerce.number().positive({
    message: "Выберите ВС из списка",
  }),
  departmental: z.coerce.boolean(),
  limit_time: z.string().min(1, { message: "Введите лимит летного времени"}).transform((val) => {
    return HHMM2min(val);
  }),
  cost: z.coerce.number().positive({
    message: "Стоимость работ должна быть выше 0",
  }),
});

const Form: FC<Props> = ({ contract_id, close }) => {
  const initialState: State = { message: null, errors: {} };
  const [state, setState] = useState<State>(initialState);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const [numberOptions, setNumberOptions] = useState<ITransport[] | null>(null);
  const [type, setType] = useState<number | null>(null);
  const [typeOptions, setTypeOptions] = useState<ITransport[]>([]);
  const [add, { isError, isLoading, isSuccess }] =
    useAddContractAircraftMutation();
  const {
    data: transport,
    isFetching: isTransportFetching,
    isError: isTransportError,
  } = useGetAllTransportQuery();

  const submit: FormEventHandler = (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    setState(initialState);
    setIsUXOpen(true);

    const validatedFields = FormSchema.safeParse({
      transport_id: formData.get("transport_id"),
      departmental: formData.get("departmental"),
      limit_time: formData.get("limit_time"),
      cost: formData.get("cost"),
    });

    if (!validatedFields.success) {
      setState({
        errors: validatedFields.error.flatten().fieldErrors,
        message: "Не удалось сохранить форму. Заполните все поля.",
      });
      return;
    }

    const { transport_id, departmental, limit_time, cost } =
      validatedFields.data;

    add({
      contract_id,
      transport_id,
      departmental,
      limit_time,
      cost,
    });
  };

  const numberHandler = (val: string) => {
    const result = transport?.find((unit) => unit.id === Number(val));
    if (result) {
      setType(result.type);
    }
  };
  const typeHandler = (val: string) => {
    if (!transport) return;
    const result = transport.filter((unit) => unit.type === Number(val));
    setType(Number(val));
    if (result.length) {
      setNumberOptions(result);
    } else {
      setNumberOptions(transport);
    }
  };

  useEffect(() => {
    if (!isSuccess) return;
    setTimeout(() => {
      close();
    }, 1500);
  }, [isSuccess]);

  useEffect(() => {
    if (!transport) return;
    const keys = [] as number[];
    const options = [] as ITransport[];
    transport.forEach((unit) => {
      if (keys.includes(unit.type)) return;
      keys.push(unit.type);
      options.push(unit);
    });
    setNumberOptions(transport);
    setTypeOptions(options);
  }, [transport]);

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.title}>Добавление воздушного судна</div>
      <div className={styles.row}>
        <label htmlFor="transport_id">Бортовой номер</label>
        <select
          className={styles.select}
          name="transport_id"
          onChange={(e) => numberHandler(e.target.value)}
          value={isTransportFetching ? "...Загрузка списка" : undefined}
        >
          <option value="">Выберите из списка</option>
          {numberOptions && !isTransportError ? (
            numberOptions.map((unit) => {
              return (
                <option value={unit.id} key={unit.id}>
                  {unit.number}
                </option>
              );
            })
          ) : (
            <option>Нет доступного транспорта</option>
          )}
        </select>
        <div id="transport_id-error" aria-live="polite" aria-atomic="true">
          {state.errors?.transport_id &&
            state.errors.transport_id.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="transport_type">Тип ВС</label>
        <select
          className={styles.select}
          name="transport_type"
          value={
            isTransportFetching ? "...Загрузка списка" : type ? type : undefined
          }
          onChange={(e) => typeHandler(e.target.value)}
        >
          <option value="">Выберите из списка</option>
          {typeOptions?.map((unit) => (
            <option value={unit.type} key={unit.id}>
              {unit.type_name}
            </option>
          ))}
        </select>
      </div>
      <div className={styles.row}>
        <label htmlFor="departmental">Ведомственное</label>
        <div className={styles.checkbox}>
          <Checkbox name="departmental" />
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="limit_time">Лимит летного времени, часы:минуты</label>
        <input
          className={styles.input}
          name="limit_time"
          placeholder="Лимит летного времени, часы:минуты"
        />
        <div id="limit_time-error" aria-live="polite" aria-atomic="true">
          {state.errors?.limit_time &&
            state.errors.limit_time.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="cost">Стоимость 1 часа, руб.</label>
        <input
          className={styles.input}
          name="cost"
          placeholder="Стоимость 1 часа, руб."
        />
        <div id="cost-error" aria-live="polite" aria-atomic="true">
          {state.errors?.cost &&
            state.errors.cost.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>

      <div className={styles.actions}>
        <Button type="reset" onClick={() => setState(initialState)}>
          Очистить
        </Button>
        <Button type="submit" isProcessing={isLoading} isDisabled={isLoading}>
          Сохранить
        </Button>
      </div>
      {state.message && isUXOpen && (
        <Snackbar
          isOpen={Boolean(state.message)}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          {state.message}
        </Snackbar>
      )}
      {isSuccess && (
        <Snackbar isOpen={isSuccess} className="success" onClose={close}>
          Изменения сохранены
        </Snackbar>
      )}
      {isError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка сохранения. Попробуйте позже
        </Snackbar>
      )}
      {isTransportError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
          autoHideDuration={3000}
        >
          Не удалось загрузить список типов ВС.
        </Snackbar>
      )}
    </form>
  );
};

export const AddContractAircraft: FC<Props> = ({ close, contract_id }) => {
  return (
    <Modal close={close}>
      <Form close={close} contract_id={contract_id} />
    </Modal>
  );
};
